import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import UnifiedFiles from 'features/bpDetails/Components/UnifiedFiles'
import { IDENTITY_FOLDER_ID } from 'features/bpDetails/Components/files/IdentityDocumentsFolder'
import {
  loadEstateFilesByFolderRoutine,
  loadEstateFilesByCategoryRoutine,
  resetEstateDocumentsRoutine
} from 'ducks/estateDocuments/actions'
import {
  selectLoadedEstateFolders,
  selectLoadingEstateFolders,
  selectEstateLoadingProgress,
  selectEstateCursor,
  selectEstateHasMore
} from 'ducks/estateDocuments/selectors'
import { selectFileCategories } from 'ducks/dictionaries/selectors'
import { getEstateFileCounts } from 'services/estateDocumentsService'
import { getFileThumbnails } from 'services/fileService'
import { updateFileThumbnailsRoutine } from 'ducks/files/actions'
import { getCurrentUser } from 'features/auth/ducks/selectors'

const EMPTY_QUERY = {}

const normalizeThumbnailKey = str => str.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()

const EMPTY_FILES = []

const EstateDocuments = ({ estate, buildings = [] }) => {
  const estateId = estate?.id
  const dispatch = useDispatch()
  const fileCategories = useSelector(selectFileCategories)
  const loadedFolders = useSelector(selectLoadedEstateFolders)
  const loadingFolders = useSelector(selectLoadingEstateFolders)
  const loadingProgress = useSelector(selectEstateLoadingProgress)
  const currentUser = useSelector(getCurrentUser)
  const store = useStore()
  const requestedThumbnailIdsRef = useRef(new Set())
  const hideDuplicates = currentUser?.hideDuplicateFiles ?? true
  const mutationVersion = useSelector(state => state.files.mutationVersion || 0)
  const prevMutationRef = useRef(mutationVersion)
  const prevHideDupRef = useRef(hideDuplicates)

  const [selectedFolderId, setSelectedFolderId] = useState(null)
  const [selectedCategoryId, setSelectedCategoryId] = useState(null)
  const currentSelectionKey = selectedCategoryId || selectedFolderId
  const filesSource = useSelector(state =>
    currentSelectionKey
      ? state.estateDocuments.estateFilesByFolder[currentSelectionKey] || EMPTY_FILES
      : EMPTY_FILES
  )
  const cursor = useSelector(selectEstateCursor(currentSelectionKey))
  const hasMore = useSelector(selectEstateHasMore(currentSelectionKey))
  const isLoadingCurrentSelection = loadingFolders.has(currentSelectionKey)

  const customBuildings = useMemo(
    () => buildings.map(bp => ({
      id: bp.id,
      name: bp.name,
      address: [bp.street, bp.city].filter(Boolean).join(', ') || undefined
    })),
    [buildings]
  )

  const [counts, setCounts] = useState(null)

  const countsSeqRef = useRef(0)
  const refetchCounts = useCallback(() => {
    if (!estateId) return
    const seq = ++countsSeqRef.current
    getEstateFileCounts({ estateId, query: hideDuplicates ? { 'filter[hide_duplicates]': 1 } : {} })
      .then(res => { if (seq === countsSeqRef.current) setCounts(res?.data?.data || null) })
      .catch(() => {})
  }, [estateId, hideDuplicates])

  useEffect(() => { refetchCounts() }, [refetchCounts])

  useEffect(() => {
    if (!filesSource || filesSource.length === 0) return

    const currentThumbnails = store.getState().files.thumbnailsById || {}
    const requested = requestedThumbnailIdsRef.current
    const filesNeedingThumbnails = filesSource.filter(
      file =>
        file.buildingPassport?.id &&
        currentThumbnails[file.id] === undefined &&
        !requested.has(file.id)
    )
    if (filesNeedingThumbnails.length === 0) return

    const fileIdsByBp = {}
    filesNeedingThumbnails.forEach(file => {
      requested.add(file.id)
      const bpId = file.buildingPassport.id
      fileIdsByBp[bpId] = fileIdsByBp[bpId] || []
      fileIdsByBp[bpId].push(file.id)
    })

    let cancelled = false

    const loadThumbnailsForBp = async (bpId, fileIds) => {
      for (let i = 0; i < fileIds.length; i += 50) {
        const batch = fileIds.slice(i, i + 50)
        try {
          const response = await getFileThumbnails(bpId, batch)
          if (cancelled) return
          const thumbnails = response?.data?.thumbnails || {}
          const batchUpdates = batch.map(fileId => {
            const normalizedFileId = normalizeThumbnailKey(fileId)
            const matchingKey = Object.keys(thumbnails).find(
              key => normalizeThumbnailKey(key) === normalizedFileId
            )
            return { id: fileId, thumbnailUrl: (matchingKey && thumbnails[matchingKey]) || null }
          })
          dispatch(updateFileThumbnailsRoutine.success(batchUpdates))
        } catch (e) {
          batch.forEach(fileId => requested.delete(fileId))
        }
      }
    }

    Object.entries(fileIdsByBp).forEach(([bpId, fileIds]) => {
      loadThumbnailsForBp(bpId, fileIds)
    })

    return () => {
      cancelled = true
    }
  }, [filesSource, dispatch, store])

  const onLoadFolder = useCallback(folderId => {
    dispatch(loadEstateFilesByFolderRoutine({ estateId, folderId, fileCategories }))
  }, [dispatch, estateId, fileCategories])

  const onLoadCategory = useCallback(categoryId => {
    dispatch(loadEstateFilesByCategoryRoutine({ estateId, categoryId }))
  }, [dispatch, estateId])

  const lastAggregateQuery = useSelector(state => state.estateDocuments.lastAggregateQuery || EMPTY_QUERY)
  const onLoadAll = useCallback(() => {
    if (selectedCategoryId) {
      dispatch(loadEstateFilesByCategoryRoutine({ estateId, categoryId: selectedCategoryId, ...lastAggregateQuery }))
    } else if (selectedFolderId === 'uncategorised' || selectedFolderId === 'all_files') {
      dispatch(loadEstateFilesByCategoryRoutine({ estateId, categoryId: selectedFolderId, ...lastAggregateQuery }))
    } else if (selectedFolderId && selectedFolderId !== IDENTITY_FOLDER_ID) {
      dispatch(loadEstateFilesByFolderRoutine({ estateId, folderId: selectedFolderId, fileCategories, ...lastAggregateQuery }))
    }
  }, [dispatch, estateId, selectedCategoryId, selectedFolderId, fileCategories, lastAggregateQuery])

  const onLoadMore = useCallback(() => {
    if (!currentSelectionKey || !hasMore || isLoadingCurrentSelection) return
    if (selectedCategoryId) {
      dispatch(loadEstateFilesByCategoryRoutine({ estateId, categoryId: selectedCategoryId, cursor }))
    } else if (selectedFolderId === 'uncategorised' || selectedFolderId === 'all_files') {
      dispatch(loadEstateFilesByCategoryRoutine({ estateId, categoryId: selectedFolderId, cursor }))
    } else if (selectedFolderId && selectedFolderId !== IDENTITY_FOLDER_ID) {
      dispatch(loadEstateFilesByFolderRoutine({ estateId, folderId: selectedFolderId, fileCategories, cursor }))
    }
  }, [dispatch, estateId, selectedCategoryId, selectedFolderId, fileCategories, cursor, hasMore, isLoadingCurrentSelection, currentSelectionKey])

  useEffect(() => {
    if (selectedFolderId !== 'all_files') return
    if (loadedFolders.has('all_files') || loadingFolders.has('all_files')) return
    dispatch(loadEstateFilesByCategoryRoutine({ estateId, categoryId: 'all_files' }))
  }, [selectedFolderId, loadedFolders, loadingFolders, estateId, dispatch])

  useEffect(() => {
    if (prevMutationRef.current === mutationVersion) return
    prevMutationRef.current = mutationVersion
    onLoadAll()
    refetchCounts()
  }, [mutationVersion, onLoadAll, refetchCounts])

  useEffect(() => {
    if (prevHideDupRef.current === hideDuplicates) return
    prevHideDupRef.current = hideDuplicates
    onLoadAll()
  }, [hideDuplicates, onLoadAll])

  useEffect(() => {
    return () => { dispatch(resetEstateDocumentsRoutine.trigger()) }
  }, [estateId, dispatch])

  const onSelectFolder = useCallback(folderId => {
    setSelectedFolderId(folderId)
  }, [])

  const onSelectCategory = useCallback(categoryId => {
    setSelectedCategoryId(categoryId)
  }, [])

  if (!estateId) return null

  return (
    <UnifiedFiles
      key={`estate:${estateId}`}
      context='estate-aggregate'
      stateKeyId={`estate:${estateId}`}
      canEdit={false}
      filesSource={filesSource}
      countsSource={counts}
      loadedFolders={loadedFolders}
      loadingFolders={loadingFolders}
      allFilesLoaded={false}
      isLoadingFiles={false}
      loadingProgress={loadingProgress}
      onLoadFolder={onLoadFolder}
      onLoadCategory={onLoadCategory}
      onLoadAll={onLoadAll}
      onSelectFolder={onSelectFolder}
      onSelectCategory={onSelectCategory}
      showCustomTab={true}
      customBuildings={customBuildings}
      hasMore={hasMore}
      loadingMore={isLoadingCurrentSelection}
      onLoadMore={onLoadMore}
    />
  )
}

export default EstateDocuments
